import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { getSalonById, type Salon } from '../../config/salons'
import { useBookingStore } from '../../store/bookingStore'
import { track } from '../../lib/analytics/track'
import { bookingVendor } from '../../lib/analytics/bookingVendor'
import { stampBookingUrl } from '../../lib/analytics/utm'
import Button from '../ui/Button'

type Variant = 'primary' | 'secondary' | 'tertiary'
type Size = 'sm' | 'md' | 'lg'
type Tone = 'light' | 'dark'

interface BookNowButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode
  /** If provided, skips the salon picker and books this branch directly. */
  salonId?: string
  /** Visual variant. Defaults to primary. */
  variant?: Variant
  /** Size. Defaults to md. */
  size?: Size
  /** Tone of the surface the button sits on. Defaults to light. */
  tone?: Tone
  /** Magnetic cursor pull (primary variant only). Defaults to false. */
  magnetic?: boolean
}

/**
 * Thin wrapper around <Button> that handles the booking flow:
 * direct branch open, preferred salon, or salon picker.
 */
const BookNowButton = forwardRef<HTMLButtonElement, BookNowButtonProps>(
  (
    {
      children,
      salonId,
      onClick,
      type = 'button',
      variant = 'primary',
      size = 'md',
      tone = 'light',
      magnetic = false,
      ...rest
    },
    ref,
  ) => {
    const open = useBookingStore((s) => s.open)
    const preferredSalonId = useBookingStore((s) => s.preferredSalonId)

    // Open the external booking system for a salon, tracking the hand-off and
    // UTM-stamping the URL. Analytics is fire-and-forget: the window.open always
    // runs regardless of whether tracking is consented.
    const openBooking = (salon: Salon) => {
      track('booking_handoff', {
        salon_id: salon.id,
        booking_vendor: bookingVendor(salon.bookingUrl),
      })
      window.open(
        stampBookingUrl(salon.bookingUrl, salon.id),
        '_blank',
        'noopener,noreferrer',
      )
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e)
      if (e.defaultPrevented) return

      track('book_now_click', {
        salon_id: salonId ?? preferredSalonId ?? 'unset',
        had_preferred: preferredSalonId != null,
      })

      // Direct branch booking — salon-scoped pages.
      if (salonId) {
        const salon = getSalonById(salonId)
        if (salon) {
          openBooking(salon)
          return
        }
      }

      // Previously chose a salon — skip picker.
      if (preferredSalonId) {
        const salon = getSalonById(preferredSalonId)
        if (salon) {
          openBooking(salon)
          return
        }
      }

      // First-time click — show picker.
      open()
    }

    return (
      <Button
        innerRef={ref}
        type={type}
        onClick={handleClick}
        variant={variant}
        size={size}
        tone={tone}
        magnetic={magnetic}
        {...rest}
      >
        {children}
      </Button>
    )
  },
)

BookNowButton.displayName = 'BookNowButton'

export default BookNowButton
